import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, emptyProps, on, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Observable, of, EMPTY } from 'rxjs';
import { withLatestFrom, flatMap, catchError, tap, map, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { PageGroup, JoiPage, PermissionAccess, AccessTypeKeys, PermissionAccessTypes, PageGroupHandle, EVENT_PERMISSIONS, PageHandle, PAGE_INFO, PageType, pageUrlsToPage, AccessType, BUDGET_GROUP, BACKUP_GROUP } from '../../../globals/store/permissions.data';
import { RouterNavigatedAction, ROUTER_NAVIGATED, routerNavigatedAction } from '@ngrx/router-store';
import { userActions } from './user.era';
import { eventsActions } from './events.era';
import { getNestedList, listUnion, makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';
export { PageGroup, JoiPage, PermissionAccess,  PermissionAccessTypes }

export const LOAD_PERMISSION = '[Permissions] loaded';
export const SET_PAGES = '[Permissions] set pages'
export const EVENT_CLOSED = '[Permissions] event closed - set all to view'
export const UNLOAD_PERMISSION = '[Permissions] unload';
export const LOAD_EVENT_WIDE_PERMISSIONS = '[Permissions] load event wide wf permissions'
export const SET_EVENT_WIDE_PERMISSIONS_FOR_SINGLE_USER = '[Permissions] SET_EVENT_WIDE_PERMISSIONS_FOR_SINGLE_USER'

export const SET_PAGE_TYPE = '[Permissions] set page type'
export const LOAD_EVENT_PERMISSIONS = '[Permissions] load event permissions'

export interface PermissionsState {
  edit: PageHandle[],
  view: PageHandle[],
  addRestrictions: PageHandle[],
  all: PageHandle[],
  readonly: PageHandle[],
  access:PermissionAccess,
  groups:PageGroup[],
  groupsByHandle: { [K in PageGroupHandle]: PageGroup }
  page_type: PageType
  openGroup: PageGroup
  openPage: JoiPage // only for inside events for now
  loading:boolean,
  loaded:boolean,
  unloading:boolean,
  eventWidePermissions:{[uid_or_email:string]: AccessType[] }
  permissionsByEvent: { [eventId:string]: AccessType[] } // for home page
}

export const makeInitialState = (): PermissionsState => ({
  edit:[],
  addRestrictions: [],
  view:[],
  all:[],
  readonly:[],
  access:{
    types:[]
  },
  groups:null,
  groupsByHandle: {} as any,
  page_type: null,
  openGroup: null,
  openPage: null,
  loaded:false,
  loading:true,
  unloading:false,
  eventWidePermissions:{},
  permissionsByEvent: {}
})

// Actions
export const permissionsActions = createActionGroup({
  source: 'Permissions',
  events: {
    'Load Permissions': (access: PermissionAccess) => ({ access }),
    'Unload Permissions': emptyProps(),
    'Set Page Type': (pageType:PageType) => ({ pageType }),
    'Load Event Wide Permissions': (payload?:{[uid_or_email:string]: AccessType[]}) => ({ payload }),
    'Add Event Wide Permissions For Single User': (uid_or_email: string, types: AccessType[]) => ({ uid_or_email, types }),
    'Load Event Permissions': (eventId: string, permissions: Array<keyof AccessTypeKeys>) => ({ eventId, permissions }),
  }
})

export const permissionsReducer = createReducer(
  { ...makeInitialState() },
  on(routerNavigatedAction, (state, { payload }: RouterNavigatedAction) => {
    const url = payload.event.urlAfterRedirects
    const page = pageUrlsToPage[url]
    if (!/^\/event\//.test(url)) return { ...state, openPage: null }
    const subUrl = (url.includes('onsite') ? url.match(/^\/event\/(onsite\/[^\/]*)\//)[1] : 
      url.includes('my_tasks') ? url.match(/^\/event\/(my_tasks\/[^\/]*)\//)[1] : 
      url.match(/^\/event\/([^\/]*)\//)[1]) || null   
    return { ...state, openPage: pageUrlsToPage[`event/${subUrl}`]}
  }),
  on(permissionsActions.loadPermissions, (state, { access }) => {
    const { edit, view, all, readonly, groups, addRestrictions } = buildPageGroups(access)
    const groupsByHandle: { [K in PageGroupHandle]: PageGroup } = makeMapFromListOfObjectsAndKey(groups, 'handle') as any
    groupsByHandle.budget = BUDGET_GROUP
    groupsByHandle.backup = BACKUP_GROUP
    return { ...state, edit, view, all, readonly, groups, groupsByHandle, access, loading:false, loaded:true, addRestrictions }
  }),
  on(permissionsActions.setPageType, (state, { pageType }) => ({
    ...state,
    page_type: pageType,
    openGroup: state.groupsByHandle[pageType]
  })),
  on(permissionsActions.loadEventWidePermissions, (state, { payload }) => {
    if(!payload) return state;
    return {...state, eventWidePermissions:payload}
  }),
  on(permissionsActions.addEventWidePermissionsForSingleUser, (state, { uid_or_email, types }) => {
    const eventWidePermissions = { ...state.eventWidePermissions }
    eventWidePermissions[uid_or_email] = types
    return { ...state, eventWidePermissions }
  }),
  on(permissionsActions.loadEventPermissions, (state, { eventId, permissions }) => {
    const permissionsByEvent = { ...state.permissionsByEvent }
    permissionsByEvent[eventId] = permissions
    return { ...state, permissionsByEvent }
  }),
  on(permissionsActions.unloadPermissions, (state) => ({ ...makeInitialState(), unloading:true })),
  on(userActions.logout, (state) => makeInitialState()),
  on(eventsActions.unloadEvent, (state) => ({ ...state, openPage: null, unloading:true })),
)

export const permissionsFeature = createFeature({
  name: 'permissions',
  reducer: permissionsReducer,
});


// Effects

@Injectable()
export class PermissionsEffects {
  // @Effect() // TIL: this fires after the reducer for EVENT_LOADED below, as all effects fire after reducers first complete
  getWorkerPermissions: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(eventsActions.loadEvent),
    tap(action => {
      console.log("Event loaded, now loading permissions for event: ", action.payload.eventId)
    }),
    withLatestFrom(this.store),
    mergeMap(  ([action, store]: [any, Store]) => {
      if (!action.payload) {
        return EMPTY
      }
      return this.http.post(`${this.api}/get-restrictions`, {eventId:action.payload.eventId}).pipe(
        map((permissions_records:{id:string, restriction:{types:AccessTypeKeys[], restrictor:string}}[])=>{
          const permissions = {}
          permissions_records.forEach(record=>{
            permissions[record.id] = record.restriction.types
          })
          return permissionsActions.loadEventWidePermissions(permissions)
        }),
				catchError(err => {
          // TODO this.log.error({file:'permissions.era',function:'getWorkerPermissions effect', response:err, data:{eventId: action.payload.eventId}})
					console.log("Error while trying to download worker permissions: ", err)
					return of(permissionsActions.loadEventWidePermissions()) // <== Ensures getting rid of the "loading" status.  Perhaps it would be good to implement retries here
				})
			)}
		)
  ));
  private api = `${environment.functionsURL}api/app`;
  constructor(public actions$:Actions, public store:Store, public http:HttpClient) {}
}


export const buildPageGroups = (access:PermissionAccess): Partial<PermissionsState> => {
  const groupPermissions = access.types.map(type => EVENT_PERMISSIONS[type]).filter(g => g)
  const edit: PageHandle[] = getNestedList(groupPermissions, 'edit')
  const view: PageHandle[] = getNestedList(groupPermissions, 'view')
  const addRestrictions: PageHandle[] = getNestedList(groupPermissions, 'addRestrictions')
  const all = listUnion(edit, view)
  const groups: PageGroup[] = []

  PAGE_INFO.forEach(group => {
    const custom_group = {...group, pages:[]}
    group.pages.forEach(page=>{
      if(edit.includes(page.handle)) {
        custom_group.pages_are_editable = true;
        custom_group.pages.push({...page, in_group:group.handle})
      }else if(view.includes(page.handle)){
        if(page.view_only_page) custom_group.pages_are_editable = true;
        const view_page:JoiPage = {...page, in_group:group.handle, is_view_only:true}
        custom_group.pages.push(view_page)
      }
    })
    if(custom_group.pages.length) groups.push(custom_group)
  })

  return { groups, edit, view, all, readonly: view, addRestrictions }
}

export const permissionsStateSelector = createFeatureSelector<PermissionsState>('permissions');
